/**
 * Keyboard utilities for handling common keyboard interactions
 * @fileoverview Common keyboard event handlers and utilities
 */

/**
 * Checks if a keyboard event represents an activation key (Enter or Space)
 * Used for interactive elements like buttons, links, and custom controls
 *
 * @param {KeyboardEvent<any>} event - The keyboard event to check
 * @returns {boolean} True if the event is Enter or Space key
 */
export function isActivationKey(event) {
  return event.key === "Enter" || event.key === " ";
}

/**
 * Checks if a keyboard event represents an escape key
 * Commonly used for closing modals, dropdowns, or canceling actions
 *
 * @param {KeyboardEvent} event - The keyboard event to check
 * @returns {boolean} True if the event is Escape key
 */
export function isEscapeKey(event) {
  return event.key === "Escape";
}

/**
 * Checks if a keyboard event represents arrow navigation keys
 *
 * @param {KeyboardEvent} event - The keyboard event to check
 * @returns {boolean} True if the event is an arrow key
 */
export function isArrowKey(event) {
  return ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
    event.key
  );
}

/**
 * Checks if a keyboard event represents a tab key
 *
 * @param {KeyboardEvent} event - The keyboard event to check
 * @returns {boolean} True if the event is Tab key
 */
export function isTabKey(event) {
  return event.key === "Tab";
}

/**
 * Checks if a keyboard event matches a specific hotkey configuration
 * Used for keyboard shortcuts and combinations
 */
export function isHotkeyMatch(event, hotkey) {
  const { ctrl, shift, alt, key } = hotkey;

  return (
    (!ctrl || event.ctrlKey) &&
    (!shift || event.shiftKey) &&
    (!alt || event.altKey) &&
    event.key.toLowerCase() === key.toLowerCase()
  );
}
